import React, { useState, useEffect, useCallback } from "react";
import {
  adminGetLeaveRequests, adminReviewLeave,
  adminGetCorrections, adminReviewCorrection,
  adminGetPermissionRequests, adminReviewPermission,
  adminGetWfhRequests, adminReviewWfh,
} from "../../../services/attendanceApi";
import Icons from "./Icons";
import { useToast } from "./Toast";

/**
 * AdminApprovals — Leave + Correction + Permission requests.
 *
 * Two views:
 *   - Pending  (default): PENDING rows only, with Approve/Reject actions.
 *   - History: all statuses (APPROVED/REJECTED/CANCELLED/PENDING),
 *              READ-ONLY with a status badge + reviewer/review note.
 */
function AdminApprovals() {
  const { toast, confirm } = useToast();
  const guard = async (decision, what) => {
    if (decision !== "REJECT") return true;
    return confirm(`Reject this ${what} request?`, "Reject request?");
  };
  const [view, setView] = useState("PENDING"); // "PENDING" | "HISTORY"
  const [leaves, setLeaves] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [permissions, setPermissions] = useState([]);
  // WFH is only a flag now (no approval workflow). Keep the state variables
  // so the rest of the component does not break, but force the section hidden.
  const [wfhRequests, setWfhRequests] = useState([]);
  const [wfhAvailable, setWfhAvailable] = useState(false); // always false — no WFH approval
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(null);

  const isHistory = view === "HISTORY";

  const load = useCallback(async () => {
    // Pending view -> PENDING only; History view -> ALL statuses.
    const statusArg = view === "HISTORY" ? "ALL" : "PENDING";
    try {
      setLoading(true);
      setError("");
      const [lr, cr, pr] = await Promise.all([
        adminGetLeaveRequests(statusArg),
        adminGetCorrections(statusArg),
        adminGetPermissionRequests(statusArg),
      ]);
      const ld = lr && lr.data ? lr.data : lr;
      const cd = cr && cr.data ? cr.data : cr;
      setLeaves(Array.isArray(ld) ? ld : (ld.requests || ld.leaves || []));
      setCorrections(Array.isArray(cd) ? cd : (cd.requests || cd.corrections || []));
      const pd = pr && pr.data ? pr.data : pr;
      setPermissions(Array.isArray(pd) ? pd : (pd.requests || pd.permissions || []));
    } catch (err) {
      setError(err.message || "Could not load approvals.");
    } finally {
      setLoading(false);
    }
    // WFH approval flow is not implemented. Keep section permanently hidden.
    setWfhRequests([]);
    setWfhAvailable(false);
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const reviewLeave = async (id, decision) => {
    if (!(await guard(decision, 'leave'))) return;
    try {
      setActing("leave-" + id);
      await adminReviewLeave(id, decision);
      await load();
      toast.success(decision === "APPROVE" ? "Request approved" : "Request rejected");
    } catch (err) {
      setError(err.message || "Action failed.");
    } finally {
      setActing(null);
    }
  };

  const reviewCorrection = async (id, decision) => {
    if (!(await guard(decision, 'correction'))) return;
    try {
      setActing("corr-" + id);
      await adminReviewCorrection(id, decision);
      await load();
      toast.success(decision === "APPROVE" ? "Request approved" : "Request rejected");
    } catch (err) {
      setError(err.message || "Action failed.");
    } finally {
      setActing(null);
    }
  };

  const reviewPermission = async (id, decision) => {
    if (!(await guard(decision, 'permission'))) return;
    try {
      setActing("perm-" + id);
      await adminReviewPermission(id, decision);
      await load();
      toast.success(decision === "APPROVE" ? "Request approved" : "Request rejected");
    } catch (err) {
      setError(err.message || "Action failed.");
    } finally {
      setActing(null);
    }
  };

  const reviewWfh = async (id, decision) => {
    if (!(await guard(decision, 'WFH'))) return;
    try {
      setActing("wfh-" + id);
      await adminReviewWfh(id, decision);
      await load();
      toast.success(decision === "APPROVE" ? "WFH request approved" : "WFH request rejected");
    } catch (err) {
      setError(err.message || "Action failed.");
    } finally {
      setActing(null);
    }
  };

  const heading = (icon, label) => (
    isHistory ? `${label} History` : `Pending ${label}`
  );

  return (
    <div className="atl-approvals">
      {/* View toggle: Pending (default) vs History */}
      <div className="atl-approvals__tabs" style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button
          className={`atl-btn atl-btn--sm ${view === "PENDING" ? "atl-btn--primary" : "atl-btn--ghost"}`}
          onClick={() => setView("PENDING")}
          disabled={view === "PENDING"}
        >
          Pending
        </button>
        <button
          className={`atl-btn atl-btn--sm ${view === "HISTORY" ? "atl-btn--primary" : "atl-btn--ghost"}`}
          onClick={() => setView("HISTORY")}
          disabled={view === "HISTORY"}
        >
          History
        </button>
      </div>

      {error && <div className="atl-error">{error}</div>}

      {loading ? (
        <div className="atl-skeleton atl-skeleton--tall" />
      ) : (
        <>
          {/* Leave Requests */}
          <h3><span className="atl-h3ic">{Icons.leave}</span>{heading(Icons.leave, "Leave Requests")}</h3>
          {leaves.length === 0 ? (
            <div className="atl-empty">{isHistory ? "No leave history." : "No pending leave requests."}</div>
          ) : (
            <div className="atl-approval-list">
              {leaves.map((l) => {
                const id = l.id;
                return (
                  <div key={id} className="atl-approval">
                    <div className="atl-approval__info">
                      <strong>{l.fullName || l.full_name || l.employeeName || "Employee"}</strong>
                      <span>{l.leaveType || l.leave_type} · {fmtDate(l.fromDate || l.from_date)} → {fmtDate(l.toDate || l.to_date)} ({l.totalDays || l.total_days} days)</span>
                      <em>{l.reason}</em>
                      {isHistory && <ReviewMeta row={l} />}
                    </div>
                    <div className="atl-approval__actions">
                      {isHistory ? (
                        <StatusBadge status={l.status} />
                      ) : (
                        <>
                          <button className="atl-btn atl-btn--sm atl-btn--primary" onClick={() => reviewLeave(id, "APPROVE")} disabled={acting === "leave-" + id}>Approve</button>
                          <button className="atl-btn atl-btn--sm atl-btn--danger" onClick={() => reviewLeave(id, "REJECT")} disabled={acting === "leave-" + id}>Reject</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Correction Requests */}
          <h3 style={{ marginTop: 24 }}><span className="atl-h3ic">{Icons.check}</span>{heading(Icons.check, "Correction Requests")}</h3>
          {corrections.length === 0 ? (
            <div className="atl-empty">{isHistory ? "No correction history." : "No pending correction requests."}</div>
          ) : (
            <div className="atl-approval-list">
              {corrections.map((cc) => {
                const id = cc.id;
                return (
                  <div key={id} className="atl-approval">
                    <div className="atl-approval__info">
                      <strong>{cc.fullName || cc.full_name || cc.employeeName || "Employee"}</strong>
                      <span>{fmtDate(cc.workDate || cc.work_date)} · In: {fmt(cc.requestedCheckIn || cc.requested_check_in)} Out: {fmt(cc.requestedCheckOut || cc.requested_check_out)}</span>
                      <em>{cc.reason}</em>
                      {isHistory && <ReviewMeta row={cc} />}
                    </div>
                    <div className="atl-approval__actions">
                      {isHistory ? (
                        <StatusBadge status={cc.status} />
                      ) : (
                        <>
                          <button className="atl-btn atl-btn--sm atl-btn--primary" onClick={() => reviewCorrection(id, "APPROVE")} disabled={acting === "corr-" + id}>Approve</button>
                          <button className="atl-btn atl-btn--sm atl-btn--danger" onClick={() => reviewCorrection(id, "REJECT")} disabled={acting === "corr-" + id}>Reject</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Permission Requests */}
          <h3 style={{ marginTop: 24 }}><span className="atl-h3ic">{Icons.permission}</span>{heading(Icons.permission, "Permission Requests")}</h3>
          {permissions.length === 0 ? (
            <div className="atl-empty">{isHistory ? "No permission history." : "No pending permission requests."}</div>
          ) : (
            <div className="atl-approval-list">
              {permissions.map((pp) => {
                const id = pp.id;
                return (
                  <div key={id} className="atl-approval">
                    <div className="atl-approval__info">
                      <strong>{pp.fullName || pp.full_name || pp.employeeName || "Employee"}</strong>
                      <span>{fmtDate(pp.permDate || pp.perm_date)} · {fmtT(pp.fromTime || pp.from_time)} → {fmtT(pp.toTime || pp.to_time)}</span>
                      <em>{pp.reason}</em>
                      {isHistory && <ReviewMeta row={pp} />}
                    </div>
                    <div className="atl-approval__actions">
                      {isHistory ? (
                        <StatusBadge status={pp.status} />
                      ) : (
                        <>
                          <button className="atl-btn atl-btn--sm atl-btn--primary" onClick={() => reviewPermission(id, "APPROVE")} disabled={acting === "perm-" + id}>Approve</button>
                          <button className="atl-btn atl-btn--sm atl-btn--danger" onClick={() => reviewPermission(id, "REJECT")} disabled={acting === "perm-" + id}>Reject</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* WFH Requests — rendered only when the backend route is available.
              WFH is an approval flow: an employee WFH check-in stays PENDING
              until approved here. Wired defensively (see load()): if the route
              404s, wfhAvailable is false and this whole block is hidden so
              nothing breaks before the backend ships. */}
          {wfhAvailable && (
            <>
              <h3 style={{ marginTop: 24 }}><span className="atl-h3ic">{Icons.team}</span>{heading(Icons.team, "WFH Requests")}</h3>
              {wfhRequests.length === 0 ? (
                <div className="atl-empty">{isHistory ? "No WFH history." : "No pending WFH requests."}</div>
              ) : (
                <div className="atl-approval-list">
                  {wfhRequests.map((w) => {
                    const id = w.id;
                    return (
                      <div key={id} className="atl-approval">
                        <div className="atl-approval__info">
                          <strong>{w.fullName || w.full_name || w.employeeName || "Employee"}</strong>
                          <span>{fmtDate(w.workDate || w.work_date || w.date)} · WFH{w.checkIn || w.check_in ? ` · In: ${fmt(w.checkIn || w.check_in)}` : ""}</span>
                          <em>{w.reason}</em>
                          {isHistory && <ReviewMeta row={w} />}
                        </div>
                        <div className="atl-approval__actions">
                          {isHistory ? (
                            <StatusBadge status={w.status} />
                          ) : (
                            <>
                              <button className="atl-btn atl-btn--sm atl-btn--primary" onClick={() => reviewWfh(id, "APPROVE")} disabled={acting === "wfh-" + id}>Approve</button>
                              <button className="atl-btn atl-btn--sm atl-btn--danger" onClick={() => reviewWfh(id, "REJECT")} disabled={acting === "wfh-" + id}>Reject</button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

    </div>
  );
}

// Status pill for history rows. Reuses atl-badge styling with status modifier.
function StatusBadge({ status }) {
  const s = String(status || "").toUpperCase();
  const cls = s === "APPROVED" ? "atl-badge--success"
    : s === "REJECTED" ? "atl-badge--danger"
    : s === "CANCELLED" ? "atl-badge--muted"
    : "atl-badge--warning";
  return <span className={`atl-badge ${cls}`}>{s || "—"}</span>;
}

// Reviewer + review note line (history only, shown when present).
function ReviewMeta({ row }) {
  const note = row.reviewNote || row.review_note;
  // Backend returns reviewed_by as a bare UUID (no display name) and
  // review_note is null for permission/correction. Never surface the raw
  // UUID to the employee — only render a human-readable review note when
  // one is actually present (leaves carry review_note). Otherwise the
  // status badge alone conveys the outcome.
  if (!note) return null;
  return (
    <em className="atl-approval__meta" style={{ opacity: 0.8 }}>
      {`Note: ${note}`}
    </em>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" });
}
function fmtT(t) {
  if (!t) return "—";
  // Accept "HH:MM" or "HH:MM:SS"
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (m) {
    let h = parseInt(m[1], 10); const min = m[2];
    const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
    return `${h}:${min} ${ap}`;
  }
  return fmt(t);
}
function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
}

export default AdminApprovals;